// ============================================================================
// Research Assistant Deep Link Utilities
// ============================================================================

export interface VisibilityContext {
  visibility_pct?: number;
  mentions?: number;
  total_runs?: number;
  rank?: number | null;
  onboarding_workspace_id?: string | null;
}

export interface CitationContext {
  domain: string;
  total_citations: number;
  unique_pages: number;
  share_pct: number;
  mentions_brand_count?: number;
  jobId?: string;
  jobIds?: string[];
}

export type ResearchContext = VisibilityContext | CitationContext;

export interface ResearchLinkParams {
  promptId?: string;
  jobId?: string;
  jobIds?: string[];
  engine?: string;
  command?: string;
  question?: string;
  context?: ResearchContext;
}

const WORKSPACE_PATH = '/app/workspace';
const LEGACY_RESEARCH_PATH = '/app/research-assistant';

function appendSearch(path: string, search: URLSearchParams): string {
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export function createWorkspaceSessionLink(
  sessionId: string,
  params: { command?: string | null; question?: string | null } = {},
): string {
  const search = new URLSearchParams();
  search.set('session', sessionId);
  if (params.command) {
    search.set('command', params.command);
  } else if (params.question) {
    search.set('question', params.question);
  }
  return appendSearch(WORKSPACE_PATH, search);
}

/**
 * Creates a deep link URL to the canonical workspace chat that will:
 * 1. Auto-create a new session for the specified prompt (or the default project workspace)
 * 2. Optionally filter by engine/platform
 * 3. Optionally auto-run a command after session creation
 * 4. Optionally auto-ask a question after session creation
 * 5. Optionally pass visibility context metrics
 *
 * @example
 * // Link to research a prompt
 * createResearchLink({ promptId: 'abc123' })
 * // => '/app/workspace?promptId=abc123'
 *
 * @example
 * // Link with engine filter and auto-command
 * createResearchLink({
 *   promptId: 'abc123',
 *   engine: 'chatgpt',
 *   command: 'analyze_competitors'
 * })
 * // => '/app/workspace?promptId=abc123&engine=chatgpt&command=analyze_competitors'
 *
 * @example
 * // Link for broad project-scoped analysis (no specific prompt)
 * createResearchLink({ command: 'analyze_publisher' })
 * // => '/app/workspace?command=analyze_publisher'
 */
export function createResearchLink(params: ResearchLinkParams): string {
  const search = new URLSearchParams();

  if (params.promptId) {
    search.set('promptId', params.promptId);
  }

  const normalizedJobIds = Array.from(
    new Set((params.jobIds || []).map(value => value.trim()).filter(Boolean)),
  );
  if (normalizedJobIds.length > 0) {
    search.set('jobIds', normalizedJobIds.join(','));
  } else if (params.jobId) {
    search.set('jobId', params.jobId);
  }

  if (params.engine) {
    search.set('engine', params.engine);
  }

  if (params.command) {
    search.set('command', params.command);
  }

  if (params.question) {
    search.set('question', params.question);
  }

  if (params.context) {
    // Encode context as base64 JSON to keep URL clean
    search.set('ctx', btoa(JSON.stringify(params.context)));
  }

  return appendSearch(WORKSPACE_PATH, search);
}

/**
 * Creates a URL link to a specific research session.
 *
 * @example
 * // Link to a session
 * createSessionLink('abc123')
 * // => '/app/workspace?session=abc123'
 *
 * @example
 * // Link to a session with a command to run
 * createSessionLink('abc123', 'analyze_competitors')
 * // => '/app/workspace?session=abc123&command=analyze_competitors'
 */
export function createSessionLink(sessionId: string, command?: string): string {
  return createWorkspaceSessionLink(sessionId, { command });
}

export function normalizeWorkspaceThreadUrl(
  threadUrl?: string | null,
  sessionId?: string | null,
): string | null {
  const fallbackSessionId = sessionId?.trim();
  const raw = threadUrl?.trim();

  if (!raw) {
    return fallbackSessionId ? createWorkspaceSessionLink(fallbackSessionId) : null;
  }

  try {
    const parsed = new URL(raw, 'https://attune.local');
    const pathname = parsed.pathname;
    const search = new URLSearchParams(parsed.search);

    if (pathname === WORKSPACE_PATH) {
      if (!search.get('session') && fallbackSessionId) {
        search.set('session', fallbackSessionId);
      }
      return appendSearch(WORKSPACE_PATH, search);
    }

    if (pathname === LEGACY_RESEARCH_PATH) {
      return appendSearch(WORKSPACE_PATH, search);
    }

    if (pathname.startsWith(`${LEGACY_RESEARCH_PATH}/`)) {
      const linkedSessionId = decodeURIComponent(
        pathname.slice(`${LEGACY_RESEARCH_PATH}/`.length).split('/')[0] || '',
      );
      if (linkedSessionId) {
        search.set('session', linkedSessionId);
      } else if (fallbackSessionId) {
        search.set('session', fallbackSessionId);
      }
      return appendSearch(WORKSPACE_PATH, search);
    }
  } catch {
    // Fall through to the raw URL if it is not parseable.
  }

  return raw;
}
